import { FlowStep, CustomerNationalIdData } from "../../interfaces/altair";
import {
  extractNationalIdFront,
  extractNationalIdBack,
  extractDrivingLicense,
  extractVehicleLicenseFront,
  extractVehicleLicenseBack,
  GeminiExtractDataResponse,
  GeminiExtractNationalIdBackResponse,
  GeminiExtractDrivingLicenseResponse,
  GeminiExtractVehicleLicenseFrontResponse,
  GeminiExtractVehicleLicenseBackResponse,
} from "../../services/nationalIdService";
import { ExtractedDataField } from "../extracted-data-confirmation";

interface CreateHandleImagesUploadedParams {
  client: any;
  flowStep: FlowStep;
  setCustomerNationalIdData: (
    updater: (prev: CustomerNationalIdData) => CustomerNationalIdData
  ) => void;
  setFlowStep?: (step: FlowStep) => void;
  onShowExtractedDataConfirmation?: (
    data: ExtractedDataField[],
    title: string,
    documentType: string,
    onConfirm: (editedData?: ExtractedDataField[]) => void,
    onSaveAndConfirm?: (editedData?: ExtractedDataField[]) => void
  ) => void;
  onMuteMic?: () => void;
  onUnmuteMic?: () => void;
}

export const createHandleImagesUploaded =
  ({
    client,
    flowStep,
    setCustomerNationalIdData,
    setFlowStep,
    onShowExtractedDataConfirmation,
    onMuteMic,
    onUnmuteMic,
  }: CreateHandleImagesUploadedParams) =>
  async (files: File[]) => {
    if (!files || files.length === 0 || !client) return;

    const file = files[0];
    const reader = new FileReader();

    reader.onload = async (e) => {
      // Get full base64 string with data:image prefix
      const fullBase64 = e.target?.result as string;
      // Extract just the base64 part for storage
      const base64 = fullBase64.split(",")[1] || fullBase64;

      console.log(`📸 [Ameen] Image uploaded, current flowStep: ${flowStep}`);

      // Mute microphone while waiting for backend API
      if (onMuteMic) {
        console.log("🔇 [Ameen] Muting microphone while waiting for backend API");
        onMuteMic();
      }

      try {
        // Call backend API to extract data based on document type
        let extractedDataFront: GeminiExtractDataResponse | undefined;
        let extractedDataBack: GeminiExtractNationalIdBackResponse | undefined;
        let extractedDrivingLicenseData: GeminiExtractDrivingLicenseResponse | undefined;
        let extractedVehicleLicenseFrontData: GeminiExtractVehicleLicenseFrontResponse | undefined;
        let extractedVehicleLicenseBackData: GeminiExtractVehicleLicenseBackResponse | undefined;
        
        if (flowStep === "national_id_front") {
          console.log(`📤 [Ameen] Calling backend API to extract National ID front data...`);
          extractedDataFront = await extractNationalIdFront(fullBase64, file.name);
          console.log(`✅ [Ameen] Backend extraction completed for front:`, extractedDataFront);
        } else if (flowStep === "national_id_back") {
          console.log(`📤 [Ameen] Calling backend API to extract National ID back data...`);
          extractedDataBack = await extractNationalIdBack(fullBase64, file.name);
          console.log(`✅ [Ameen] Backend extraction completed for back:`, extractedDataBack);
        } else if (flowStep === "driving_license") {
          console.log(`📤 [Ameen] Calling backend API to extract Driving License data...`);
          extractedDrivingLicenseData = await extractDrivingLicense(fullBase64, file.name);
          console.log(`✅ [Ameen] Backend extraction completed for driving license:`, extractedDrivingLicenseData);
        } else if (flowStep === "vehicle_license_front") {
          console.log(`📤 [Ameen] Calling backend API to extract Vehicle License front data...`);
          extractedVehicleLicenseFrontData = await extractVehicleLicenseFront(fullBase64, file.name);
          console.log(`✅ [Ameen] Backend extraction completed for vehicle license front:`, extractedVehicleLicenseFrontData);
        } else if (flowStep === "vehicle_license_back") {
          console.log(`📤 [Ameen] Calling backend API to extract Vehicle License back data...`);
          extractedVehicleLicenseBackData = await extractVehicleLicenseBack(fullBase64, file.name);
          console.log(`✅ [Ameen] Backend extraction completed for vehicle license back:`, extractedVehicleLicenseBackData);
        }

        // Update state with extracted data and image
        setCustomerNationalIdData((prev) => {
          const updated = flowStep === "national_id_front" && extractedDataFront
            ? {
                ...prev,
                national_id_front_image_base64: base64,
                // Update with extracted data from backend (front side)
                full_name: extractedDataFront.fullName || prev.full_name,
                address: extractedDataFront.address || prev.address,
                national_id_number: extractedDataFront.nationalIdNumber || prev.national_id_number,
                card_serial_number: extractedDataFront.cardSerialNumber || prev.card_serial_number,
                birth_date: extractedDataFront.birthDate || prev.birth_date,
              }
            : flowStep === "national_id_back" && extractedDataBack
            ? {
                ...prev,
                national_id_back_image_base64: base64,
                // Update with extracted data from backend (back side)
                national_id_number: extractedDataBack.nationaIdNumber || prev.national_id_number,
                gender: extractedDataBack.gender || prev.gender,
                religion: extractedDataBack.religion || prev.religion,
                marital_status: extractedDataBack.maritalStatus || prev.marital_status,
                profession: extractedDataBack.professionQualification || prev.profession,
                expiration_date: extractedDataBack.dateOfExpiry || prev.expiration_date,
                issue_batch_date: extractedDataBack.dateOfIssue || prev.issue_batch_date,
              }
            : flowStep === "national_id_front"
            ? {
                ...prev,
                national_id_front_image_base64: base64,
              }
            : flowStep === "national_id_back"
            ? {
                ...prev,
                national_id_back_image_base64: base64,
              }
            : prev;

          // Save to session storage when both images are uploaded
          // Only save text data, not base64 images (to avoid quota exceeded error)
          if (
            flowStep === "national_id_back" &&
            updated.national_id_front_image_base64 &&
            updated.national_id_back_image_base64
          ) {
            try {
              const existingData = sessionStorage.getItem("saaed_user_data");
              const userData = existingData ? JSON.parse(existingData) : {};

              // Extract only text fields (exclude base64 images to avoid storage quota issues)
              const textOnlyData = {
                national_id_number: updated.national_id_number,
                profession: updated.profession,
                gender: updated.gender,
                religion: updated.religion,
                marital_status: updated.marital_status,
                expiration_date: updated.expiration_date,
                issue_batch_date: updated.issue_batch_date,
                full_name: updated.full_name,
                address: updated.address,
                birth_date: updated.birth_date,
                card_serial_number: updated.card_serial_number,
                // Note: Images are stored in component state, not in sessionStorage
                // to avoid QuotaExceededError
              };

              // Merge customer national ID data into user data
              const updatedUserData = {
                ...userData,
                customerNationalIdData: textOnlyData,
              };

              sessionStorage.setItem(
                "saaed_user_data",
                JSON.stringify(updatedUserData)
              );
              console.log(
                "✅ [Ameen] Customer National ID data (text only) saved to session storage"
              );
            } catch (error) {
              console.error(
                "❌ [Ameen] Failed to save to session storage:",
                error
              );
            }
          }

          return updated;
        });

        // Save vehicle license front data to session storage when front is uploaded
        if (flowStep === "vehicle_license_front" && extractedVehicleLicenseFrontData) {
          try {
            const existingData = sessionStorage.getItem("saaed_user_data");
            const userData = existingData ? JSON.parse(existingData) : {};

            // Save front side data temporarily
            const vehicleLicenseFrontData = {
              issuingTrafficDepartment: extractedVehicleLicenseFrontData.issuingTrafficDepartment || "",
              issuingTrafficUnit: extractedVehicleLicenseFrontData.issuingTrafficUnit || "",
              ownerName: extractedVehicleLicenseFrontData.ownerName || "",
              ownerAddress: extractedVehicleLicenseFrontData.ownerAddress || "",
              ownerNationality: extractedVehicleLicenseFrontData.ownerNationality || "",
              licenseType: extractedVehicleLicenseFrontData.licenseType || "",
              licenseIssueDate: extractedVehicleLicenseFrontData.licenseIssueDate || "",
              licenseExpiryDate: extractedVehicleLicenseFrontData.licenseExpiryDate || "",
              vehiclePlateNumber: extractedVehicleLicenseFrontData.vehiclePlateNumber || "",
            };

            const updatedUserData = {
              ...userData,
              customerVehicleLicenseFrontData: vehicleLicenseFrontData,
            };

            sessionStorage.setItem(
              "saaed_user_data",
              JSON.stringify(updatedUserData)
            );
            console.log(
              "✅ [Ameen] Customer Vehicle License front data saved to session storage"
            );
          } catch (error) {
            console.error(
              "❌ [Ameen] Failed to save vehicle license front data to session storage:",
              error
            );
          }
        }

        // Save complete vehicle license data to session storage when both sides are uploaded
        if (flowStep === "vehicle_license_back" && extractedVehicleLicenseBackData) {
          try {
            const existingData = sessionStorage.getItem("saaed_user_data");
            const userData = existingData ? JSON.parse(existingData) : {};

            // Get front data from session storage (saved when front was uploaded)
            const frontData = userData.customerVehicleLicenseFrontData || {};

            // Extract only text fields (exclude base64 images to avoid storage quota issues)
            const vehicleLicenseData = {
              // Front side data (from session storage)
              issuingTrafficDepartment: frontData.issuingTrafficDepartment || "",
              issuingTrafficUnit: frontData.issuingTrafficUnit || "",
              ownerName: frontData.ownerName || "",
              ownerAddress: frontData.ownerAddress || "",
              ownerNationality: frontData.ownerNationality || "",
              licenseType: frontData.licenseType || "",
              licenseIssueDate: frontData.licenseIssueDate || "",
              licenseExpiryDate: frontData.licenseExpiryDate || "",
              vehiclePlateNumber: frontData.vehiclePlateNumber || "",
              // Back side data (from current extraction)
              make: extractedVehicleLicenseBackData.make || "",
              model: extractedVehicleLicenseBackData.model || "",
              chassisNumber: extractedVehicleLicenseBackData.chassisNumber || "",
              motorNumber: extractedVehicleLicenseBackData.motorNumber || "",
              engineCylinders: extractedVehicleLicenseBackData.engineCylinders || "",
              fuelType: extractedVehicleLicenseBackData.fuelType || "",
              engineCapacity: extractedVehicleLicenseBackData.engineCapacity || "",
              features: extractedVehicleLicenseBackData.features || "",
              code: extractedVehicleLicenseBackData.code || "",
              modelYear: extractedVehicleLicenseBackData.modelYear || "",
              bodyStyle: extractedVehicleLicenseBackData.bodyStyle || "",
              inspectionExpiryYear: extractedVehicleLicenseBackData.inspectionExpiryYear || "",
              vehicleColor: extractedVehicleLicenseBackData.vehicleColor || "",
              // Note: Images are stored in component state, not in sessionStorage
              // to avoid QuotaExceededError
            };

            // Merge complete vehicle license data into user data
            const updatedUserData = {
              ...userData,
              customerVehicleLicenseData: vehicleLicenseData,
              // Remove temporary front data
              customerVehicleLicenseFrontData: undefined,
            };

            sessionStorage.setItem(
              "saaed_user_data",
              JSON.stringify(updatedUserData)
            );
            console.log(
              "✅ [Ameen] Customer Vehicle License data (text only) saved to session storage"
            );
          } catch (error) {
            console.error(
              "❌ [Ameen] Failed to save vehicle license data to session storage:",
              error
            );
          }
        }

        // Helper function to save extracted data to session storage (same as UI updates)
        const saveExtractedDataToSession = (data: any, flowStep: FlowStep) => {
          try {
            const existingData = sessionStorage.getItem("saaed_user_data");
            const userData = existingData ? JSON.parse(existingData) : {};

            if (flowStep === "national_id_front" && data) {
              const textOnlyData = {
                full_name: data.fullName || "",
                address: data.address || "",
                national_id_number: data.nationalIdNumber || "",
                card_serial_number: data.cardSerialNumber || "",
                birth_date: data.birthDate || "",
              };
              const updatedUserData = {
                ...userData,
                customerNationalIdData: {
                  ...(userData.customerNationalIdData || {}),
                  ...textOnlyData,
                },
              };
              sessionStorage.setItem("saaed_user_data", JSON.stringify(updatedUserData));
              console.log("✅ [Ameen] Saved National ID front data to session storage");
            } else if (flowStep === "national_id_back" && data) {
              const textOnlyData = {
                national_id_number: data.nationaIdNumber || "",
                gender: data.gender || "",
                religion: data.religion || "",
                marital_status: data.maritalStatus || "",
                profession: data.professionQualification || "",
                expiration_date: data.dateOfExpiry || "",
                issue_batch_date: data.dateOfIssue || "",
              };
              const updatedUserData = {
                ...userData,
                customerNationalIdData: {
                  ...(userData.customerNationalIdData || {}),
                  ...textOnlyData,
                },
              };
              sessionStorage.setItem("saaed_user_data", JSON.stringify(updatedUserData));
              console.log("✅ [Ameen] Saved National ID back data to session storage");
            } else if (flowStep === "driving_license" && data) {
              const drivingLicenseData = {
                issuingAuthority: data.issuingAuthority || "",
                issuingOffice: data.issuingOffice || "",
                licenseType: data.licenseType || "",
                nationalId: data.nationalId || "",
                nameArabic: data.nameArabic || "",
                nameEnglish: data.nameEnglish || "",
                address: data.address || "",
                nationalityArabic: data.nationalityArabic || "",
                nationalityEnglish: data.nationalityEnglish || "",
                occupation: data.occupation || "",
                dateOfIssue: data.dateOfIssue || "",
                dateOfExpiry: data.dateOfExpiry || "",
                licenseClass: data.licenseClass || "",
              };
              const updatedUserData = {
                ...userData,
                customerDrivingLicenseData: drivingLicenseData,
              };
              sessionStorage.setItem("saaed_user_data", JSON.stringify(updatedUserData));
              console.log("✅ [Ameen] Saved Driving License data to session storage");
            } else if (flowStep === "vehicle_license_front" && data) {
              const vehicleLicenseFrontData = {
                issuingTrafficDepartment: data.issuingTrafficDepartment || "",
                issuingTrafficUnit: data.issuingTrafficUnit || "",
                ownerName: data.ownerName || "",
                ownerAddress: data.ownerAddress || "",
                ownerNationality: data.ownerNationality || "",
                licenseType: data.licenseType || "",
                licenseIssueDate: data.licenseIssueDate || "",
                licenseExpiryDate: data.licenseExpiryDate || "",
                vehiclePlateNumber: data.vehiclePlateNumber || "",
              };
              const updatedUserData = {
                ...userData,
                customerVehicleLicenseFrontData: vehicleLicenseFrontData,
              };
              sessionStorage.setItem("saaed_user_data", JSON.stringify(updatedUserData));
              console.log("✅ [Ameen] Saved Vehicle License front data to session storage");
            } else if (flowStep === "vehicle_license_back" && data) {
              const frontData = userData.customerVehicleLicenseFrontData || {};
              const vehicleLicenseData = {
                ...frontData,
                make: data.make || "",
                model: data.model || "",
                chassisNumber: data.chassisNumber || "",
                motorNumber: data.motorNumber || "",
                engineCylinders: data.engineCylinders || "",
                fuelType: data.fuelType || "",
                engineCapacity: data.engineCapacity || "",
                features: data.features || "",
                code: data.code || "",
                modelYear: data.modelYear || "",
                bodyStyle: data.bodyStyle || "",
                inspectionExpiryYear: data.inspectionExpiryYear || "",
                vehicleColor: data.vehicleColor || "",
              };
              const updatedUserData = {
                ...userData,
                customerVehicleLicenseData: vehicleLicenseData,
                customerVehicleLicenseFrontData: undefined,
              };
              sessionStorage.setItem("saaed_user_data", JSON.stringify(updatedUserData));
              console.log("✅ [Ameen] Saved Vehicle License back data to session storage");
            }
          } catch (error) {
            console.error("❌ [Ameen] Failed to save extracted data to session storage:", error);
          }
        };

        // Helper function to convert edited data fields back to data object
        const convertEditedFieldsToData = (
          editedFields: ExtractedDataField[] | undefined,
          originalData: any,
          flowStep: FlowStep
        ): any => {
          if (!editedFields || editedFields.length === 0) {
            return originalData;
          }

          // Create a map of label to value for quick lookup
          const fieldMap = new Map<string, string>();
          editedFields.forEach(field => {
            fieldMap.set(field.label, field.value || "");
          });

          // Convert based on flowStep
          if (flowStep === "national_id_front") {
            return {
              ...originalData,
              fullName: fieldMap.get("الاسم الكامل") || originalData.fullName || "",
              address: fieldMap.get("العنوان") || originalData.address || "",
              nationalIdNumber: fieldMap.get("رقم الرقم القومي") || originalData.nationalIdNumber || "",
              cardSerialNumber: fieldMap.get("رقم البطاقة") || originalData.cardSerialNumber || "",
              birthDate: fieldMap.get("تاريخ الميلاد") || originalData.birthDate || "",
            };
          } else if (flowStep === "national_id_back") {
            return {
              ...originalData,
              nationaIdNumber: fieldMap.get("رقم الرقم القومي") || originalData.nationaIdNumber || "",
              gender: fieldMap.get("الجنس") || originalData.gender || "",
              religion: fieldMap.get("الدين") || originalData.religion || "",
              maritalStatus: fieldMap.get("الحالة الاجتماعية") || originalData.maritalStatus || "",
              professionQualification: fieldMap.get("المهنة") || originalData.professionQualification || "",
              dateOfIssue: fieldMap.get("تاريخ الإصدار") || originalData.dateOfIssue || "",
              dateOfExpiry: fieldMap.get("تاريخ الانتهاء") || originalData.dateOfExpiry || "",
            };
          } else if (flowStep === "driving_license") {
            return {
              ...originalData,
              issuingAuthority: fieldMap.get("السلطة المصدرة") || originalData.issuingAuthority || "",
              issuingOffice: fieldMap.get("المكتب المصدر") || originalData.issuingOffice || "",
              licenseType: fieldMap.get("نوع الرخصة") || originalData.licenseType || "",
              nationalId: fieldMap.get("الرقم القومي") || originalData.nationalId || "",
              nameArabic: fieldMap.get("الاسم (عربي)") || originalData.nameArabic || "",
              nameEnglish: fieldMap.get("الاسم (إنجليزي)") || originalData.nameEnglish || "",
              address: fieldMap.get("العنوان") || originalData.address || "",
              nationalityArabic: fieldMap.get("الجنسية (عربي)") || originalData.nationalityArabic || "",
              nationalityEnglish: fieldMap.get("الجنسية (إنجليزي)") || originalData.nationalityEnglish || "",
              occupation: fieldMap.get("المهنة") || originalData.occupation || "",
              dateOfIssue: fieldMap.get("تاريخ الإصدار") || originalData.dateOfIssue || "",
              dateOfExpiry: fieldMap.get("تاريخ الانتهاء") || originalData.dateOfExpiry || "",
              licenseClass: fieldMap.get("فئة الرخصة") || originalData.licenseClass || "",
            };
          } else if (flowStep === "vehicle_license_front") {
            return {
              ...originalData,
              issuingTrafficDepartment: fieldMap.get("إدارة المرور المصدرة") || originalData.issuingTrafficDepartment || "",
              issuingTrafficUnit: fieldMap.get("وحدة المرور المصدرة") || originalData.issuingTrafficUnit || "",
              ownerName: fieldMap.get("اسم المالك") || originalData.ownerName || "",
              ownerAddress: fieldMap.get("عنوان المالك") || originalData.ownerAddress || "",
              ownerNationality: fieldMap.get("جنسية المالك") || originalData.ownerNationality || "",
              licenseType: fieldMap.get("نوع الرخصة") || originalData.licenseType || "",
              licenseIssueDate: fieldMap.get("تاريخ الإصدار") || originalData.licenseIssueDate || "",
              licenseExpiryDate: fieldMap.get("تاريخ الانتهاء") || originalData.licenseExpiryDate || "",
              vehiclePlateNumber: fieldMap.get("رقم اللوحة") || originalData.vehiclePlateNumber || "",
            };
          } else if (flowStep === "vehicle_license_back") {
            return {
              ...originalData,
              make: fieldMap.get("الماركة") || originalData.make || "",
              model: fieldMap.get("الموديل") || originalData.model || "",
              chassisNumber: fieldMap.get("رقم الشاصي") || originalData.chassisNumber || "",
              motorNumber: fieldMap.get("رقم المحرك") || originalData.motorNumber || "",
              engineCylinders: fieldMap.get("عدد أسطوانات المحرك") || originalData.engineCylinders || "",
              fuelType: fieldMap.get("نوع الوقود") || originalData.fuelType || "",
              engineCapacity: fieldMap.get("سعة المحرك") || originalData.engineCapacity || "",
              features: fieldMap.get("المميزات") || originalData.features || "",
              code: fieldMap.get("الكود") || originalData.code || "",
              modelYear: fieldMap.get("سنة الصنع") || originalData.modelYear || "",
              bodyStyle: fieldMap.get("نوع الهيكل") || originalData.bodyStyle || "",
              inspectionExpiryYear: fieldMap.get("سنة انتهاء الفحص") || originalData.inspectionExpiryYear || "",
              vehicleColor: fieldMap.get("لون السيارة") || originalData.vehicleColor || "",
            };
          }

          return originalData;
        };

        // Helper function to create extracted data string from data object
        const createExtractedDataString = (data: any, flowStep: FlowStep): string => {
          if (flowStep === "national_id_front") {
            return `"Full Name": "${data.fullName || ""}", "Address": "${data.address || ""}", "National ID Number": "${data.nationalIdNumber || ""}", "Card Serial Number": "${data.cardSerialNumber || ""}", "Birth Date": "${data.birthDate || ""}"`;
          } else if (flowStep === "national_id_back") {
            return `"National ID Number": "${data.nationaIdNumber || ""}", "Gender": "${data.gender || ""}", "Religion": "${data.religion || ""}", "Marital Status": "${data.maritalStatus || ""}", "Profession": "${data.professionQualification || ""}", "Date of Issue": "${data.dateOfIssue || ""}", "Date of Expiry": "${data.dateOfExpiry || ""}"`;
          } else if (flowStep === "driving_license") {
            return `"Issuing Authority": "${data.issuingAuthority || ""}", "Issuing Office": "${data.issuingOffice || ""}", "License Type": "${data.licenseType || ""}", "National ID": "${data.nationalId || ""}", "Name (Arabic)": "${data.nameArabic || ""}", "Name (English)": "${data.nameEnglish || ""}", "Address": "${data.address || ""}", "Nationality (Arabic)": "${data.nationalityArabic || ""}", "Nationality (English)": "${data.nationalityEnglish || ""}", "Occupation": "${data.occupation || ""}", "Date of Issue": "${data.dateOfIssue || ""}", "Date of Expiry": "${data.dateOfExpiry || ""}", "License Class": "${data.licenseClass || ""}"`;
          } else if (flowStep === "vehicle_license_front") {
            return `"Issuing Traffic Department": "${data.issuingTrafficDepartment || ""}", "Issuing Traffic Unit": "${data.issuingTrafficUnit || ""}", "Owner Name": "${data.ownerName || ""}", "Owner Address": "${data.ownerAddress || ""}", "Owner Nationality": "${data.ownerNationality || ""}", "License Type": "${data.licenseType || ""}", "License Issue Date": "${data.licenseIssueDate || ""}", "License Expiry Date": "${data.licenseExpiryDate || ""}", "Vehicle Plate Number": "${data.vehiclePlateNumber || ""}"`;
          } else if (flowStep === "vehicle_license_back") {
            return `"Make": "${data.make || ""}", "Model": "${data.model || ""}", "Chassis Number": "${data.chassisNumber || ""}", "Motor Number": "${data.motorNumber || ""}", "Engine Cylinders": "${data.engineCylinders || ""}", "Fuel Type": "${data.fuelType || ""}", "Engine Capacity": "${data.engineCapacity || ""}", "Features": "${data.features || ""}", "Code": "${data.code || ""}", "Model Year": "${data.modelYear || ""}", "Body Style": "${data.bodyStyle || ""}", "Inspection Expiry Year": "${data.inspectionExpiryYear || ""}", "Vehicle Color": "${data.vehicleColor || ""}"`;
          }
          return "";
        };

        // Helper function to send system_ui message and proceed
        const proceedWithExtractedData = (
          extractedDataString: string,
          systemUiEvent: string
        ) => {
          console.log(`📤 [Ameen] Sending [system_ui] ${systemUiEvent} with extracted data`);
          
          // Unmute microphone after backend API completes
          if (onUnmuteMic) {
            console.log("🔊 [Ameen] Unmuting microphone after backend API completion");
            onUnmuteMic();
          }
          
          client.send(
            [
              {
                text: `[system_ui] ${systemUiEvent}: ${extractedDataString}`,
              },
            ],
            false
          );
          
          // Send follow-up instruction based on which document was uploaded
          setTimeout(() => {
            let nextInstruction = "";
            
            if (systemUiEvent === "user_uploaded_national_id_front") {
              nextInstruction = "[SYSTEM INSTRUCTION] Front National ID uploaded successfully. Now call request_national_id_back to request the back side.";
            } else if (systemUiEvent === "user_uploaded_national_id_back") {
              nextInstruction = "[SYSTEM INSTRUCTION] Back National ID uploaded successfully. Now call request_driving_license to request the driving license.";
            } else if (systemUiEvent === "user_uploaded_driving_license") {
              nextInstruction = "[SYSTEM INSTRUCTION] Driving license uploaded successfully. Now call request_vehicle_license_front to request the front of the vehicle license.";
            } else if (systemUiEvent === "user_uploaded_vehicle_license_front") {
              nextInstruction = "[SYSTEM INSTRUCTION] Front vehicle license uploaded successfully. Now call request_vehicle_license_back to request the back side.";
            } else if (systemUiEvent === "user_uploaded_vehicle_license_back") {
              nextInstruction = "[SYSTEM INSTRUCTION] Back vehicle license uploaded successfully. Now call show_policy_survey_options to let the user choose survey method.";
            }
            
            if (nextInstruction) {
              client.send(
                [
                  {
                    text: nextInstruction,
                  },
                ],
                true
              );
            }
          }, 300);
          
          if (setFlowStep) {
            setFlowStep("idle");
          }
        };

        // Show confirmation modal for extracted data before proceeding
        if (flowStep === "national_id_front" && extractedDataFront) {
          const extractedDataFields: ExtractedDataField[] = [
            { label: "الاسم الكامل", value: extractedDataFront.fullName || "" },
            { label: "العنوان", value: extractedDataFront.address || "" },
            { label: "رقم الرقم القومي", value: extractedDataFront.nationalIdNumber || "" },
            { label: "رقم البطاقة", value: extractedDataFront.cardSerialNumber || "" },
            { label: "تاريخ الميلاد", value: extractedDataFront.birthDate || "" },
          ];
          const extractedDataString = `"Full Name": "${extractedDataFront.fullName || ""}", "Address": "${extractedDataFront.address || ""}", "National ID Number": "${extractedDataFront.nationalIdNumber || ""}", "Card Serial Number": "${extractedDataFront.cardSerialNumber || ""}", "Birth Date": "${extractedDataFront.birthDate || ""}"`;
          
          if (onShowExtractedDataConfirmation) {
            onShowExtractedDataConfirmation(
              extractedDataFields,
              "تأكيد بيانات بطاقة الرقم القومي - الوجه الأمامي",
              "بطاقة الرقم القومي - الوجه الأمامي",
              (editedData) => {
                const finalData = convertEditedFieldsToData(editedData, extractedDataFront, "national_id_front");
                const dataString = createExtractedDataString(finalData, "national_id_front");
                // Update state with edited data
                setCustomerNationalIdData((prev) => ({
                  ...prev,
                  full_name: finalData.fullName || prev.full_name,
                  address: finalData.address || prev.address,
                  national_id_number: finalData.nationalIdNumber || prev.national_id_number,
                  card_serial_number: finalData.cardSerialNumber || prev.card_serial_number,
                  birth_date: finalData.birthDate || prev.birth_date,
                }));
                proceedWithExtractedData(dataString, "user_uploaded_national_id_front");
              },
              (editedData) => {
                const finalData = convertEditedFieldsToData(editedData, extractedDataFront, "national_id_front");
                const dataString = createExtractedDataString(finalData, "national_id_front");
                // Update state with edited data
                setCustomerNationalIdData((prev) => ({
                  ...prev,
                  full_name: finalData.fullName || prev.full_name,
                  address: finalData.address || prev.address,
                  national_id_number: finalData.nationalIdNumber || prev.national_id_number,
                  card_serial_number: finalData.cardSerialNumber || prev.card_serial_number,
                  birth_date: finalData.birthDate || prev.birth_date,
                }));
                saveExtractedDataToSession(finalData, "national_id_front");
                proceedWithExtractedData(dataString, "user_uploaded_national_id_front");
              }
            );
          } else {
            // Fallback: proceed immediately if no confirmation callback
            proceedWithExtractedData(extractedDataString, "user_uploaded_national_id_front");
          }
        } else if (flowStep === "national_id_back" && extractedDataBack) {
          const extractedDataFields: ExtractedDataField[] = [
            { label: "رقم الرقم القومي", value: extractedDataBack.nationaIdNumber || "" },
            { label: "الجنس", value: extractedDataBack.gender || "" },
            { label: "الدين", value: extractedDataBack.religion || "" },
            { label: "الحالة الاجتماعية", value: extractedDataBack.maritalStatus || "" },
            { label: "المهنة", value: extractedDataBack.professionQualification || "" },
            { label: "تاريخ الإصدار", value: extractedDataBack.dateOfIssue || "" },
            { label: "تاريخ الانتهاء", value: extractedDataBack.dateOfExpiry || "" },
          ];
          const extractedDataString = `"National ID Number": "${extractedDataBack.nationaIdNumber || ""}", "Gender": "${extractedDataBack.gender || ""}", "Religion": "${extractedDataBack.religion || ""}", "Marital Status": "${extractedDataBack.maritalStatus || ""}", "Profession": "${extractedDataBack.professionQualification || ""}", "Date of Issue": "${extractedDataBack.dateOfIssue || ""}", "Date of Expiry": "${extractedDataBack.dateOfExpiry || ""}"`;
          
          if (onShowExtractedDataConfirmation) {
            onShowExtractedDataConfirmation(
              extractedDataFields,
              "تأكيد بيانات بطاقة الرقم القومي - الوجه الخلفي",
              "بطاقة الرقم القومي - الوجه الخلفي",
              (editedData) => {
                const finalData = convertEditedFieldsToData(editedData, extractedDataBack, "national_id_back");
                const dataString = createExtractedDataString(finalData, "national_id_back");
                // Update state with edited data
                setCustomerNationalIdData((prev) => ({
                  ...prev,
                  national_id_number: finalData.nationaIdNumber || prev.national_id_number,
                  gender: finalData.gender || prev.gender,
                  religion: finalData.religion || prev.religion,
                  marital_status: finalData.maritalStatus || prev.marital_status,
                  profession: finalData.professionQualification || prev.profession,
                  expiration_date: finalData.dateOfExpiry || prev.expiration_date,
                  issue_batch_date: finalData.dateOfIssue || prev.issue_batch_date,
                }));
                proceedWithExtractedData(dataString, "user_uploaded_national_id_back");
              },
              (editedData) => {
                const finalData = convertEditedFieldsToData(editedData, extractedDataBack, "national_id_back");
                const dataString = createExtractedDataString(finalData, "national_id_back");
                // Update state with edited data
                setCustomerNationalIdData((prev) => ({
                  ...prev,
                  national_id_number: finalData.nationaIdNumber || prev.national_id_number,
                  gender: finalData.gender || prev.gender,
                  religion: finalData.religion || prev.religion,
                  marital_status: finalData.maritalStatus || prev.marital_status,
                  profession: finalData.professionQualification || prev.profession,
                  expiration_date: finalData.dateOfExpiry || prev.expiration_date,
                  issue_batch_date: finalData.dateOfIssue || prev.issue_batch_date,
                }));
                saveExtractedDataToSession(finalData, "national_id_back");
                proceedWithExtractedData(dataString, "user_uploaded_national_id_back");
              }
            );
          } else {
            proceedWithExtractedData(extractedDataString, "user_uploaded_national_id_back");
          }
        } else if (flowStep === "driving_license" && extractedDrivingLicenseData) {
          const extractedDataFields: ExtractedDataField[] = [
            { label: "السلطة المصدرة", value: extractedDrivingLicenseData.issuingAuthority || "" },
            { label: "المكتب المصدر", value: extractedDrivingLicenseData.issuingOffice || "" },
            { label: "نوع الرخصة", value: extractedDrivingLicenseData.licenseType || "" },
            { label: "الرقم القومي", value: extractedDrivingLicenseData.nationalId || "" },
            { label: "الاسم (عربي)", value: extractedDrivingLicenseData.nameArabic || "" },
            { label: "الاسم (إنجليزي)", value: extractedDrivingLicenseData.nameEnglish || "" },
            { label: "العنوان", value: extractedDrivingLicenseData.address || "" },
            { label: "الجنسية (عربي)", value: extractedDrivingLicenseData.nationalityArabic || "" },
            { label: "الجنسية (إنجليزي)", value: extractedDrivingLicenseData.nationalityEnglish || "" },
            { label: "المهنة", value: extractedDrivingLicenseData.occupation || "" },
            { label: "تاريخ الإصدار", value: extractedDrivingLicenseData.dateOfIssue || "" },
            { label: "تاريخ الانتهاء", value: extractedDrivingLicenseData.dateOfExpiry || "" },
            { label: "فئة الرخصة", value: extractedDrivingLicenseData.licenseClass || "" },
          ];
          const extractedDataString = `"Issuing Authority": "${extractedDrivingLicenseData.issuingAuthority || ""}", "Issuing Office": "${extractedDrivingLicenseData.issuingOffice || ""}", "License Type": "${extractedDrivingLicenseData.licenseType || ""}", "National ID": "${extractedDrivingLicenseData.nationalId || ""}", "Name (Arabic)": "${extractedDrivingLicenseData.nameArabic || ""}", "Name (English)": "${extractedDrivingLicenseData.nameEnglish || ""}", "Address": "${extractedDrivingLicenseData.address || ""}", "Nationality (Arabic)": "${extractedDrivingLicenseData.nationalityArabic || ""}", "Nationality (English)": "${extractedDrivingLicenseData.nationalityEnglish || ""}", "Occupation": "${extractedDrivingLicenseData.occupation || ""}", "Date of Issue": "${extractedDrivingLicenseData.dateOfIssue || ""}", "Date of Expiry": "${extractedDrivingLicenseData.dateOfExpiry || ""}", "License Class": "${extractedDrivingLicenseData.licenseClass || ""}"`;
          
          if (onShowExtractedDataConfirmation) {
            onShowExtractedDataConfirmation(
              extractedDataFields,
              "تأكيد بيانات رخصة القيادة",
              "رخصة القيادة",
              (editedData) => {
                const finalData = convertEditedFieldsToData(editedData, extractedDrivingLicenseData, "driving_license");
                const dataString = createExtractedDataString(finalData, "driving_license");
                proceedWithExtractedData(dataString, "user_uploaded_driving_license");
              },
              (editedData) => {
                const finalData = convertEditedFieldsToData(editedData, extractedDrivingLicenseData, "driving_license");
                const dataString = createExtractedDataString(finalData, "driving_license");
                saveExtractedDataToSession(finalData, "driving_license");
                proceedWithExtractedData(dataString, "user_uploaded_driving_license");
              }
            );
          } else {
            proceedWithExtractedData(extractedDataString, "user_uploaded_driving_license");
          }
        } else if (flowStep === "vehicle_license_front" && extractedVehicleLicenseFrontData) {
          const extractedDataFields: ExtractedDataField[] = [
            { label: "إدارة المرور المصدرة", value: extractedVehicleLicenseFrontData.issuingTrafficDepartment || "" },
            { label: "وحدة المرور المصدرة", value: extractedVehicleLicenseFrontData.issuingTrafficUnit || "" },
            { label: "اسم المالك", value: extractedVehicleLicenseFrontData.ownerName || "" },
            { label: "عنوان المالك", value: extractedVehicleLicenseFrontData.ownerAddress || "" },
            { label: "جنسية المالك", value: extractedVehicleLicenseFrontData.ownerNationality || "" },
            { label: "نوع الرخصة", value: extractedVehicleLicenseFrontData.licenseType || "" },
            { label: "تاريخ الإصدار", value: extractedVehicleLicenseFrontData.licenseIssueDate || "" },
            { label: "تاريخ الانتهاء", value: extractedVehicleLicenseFrontData.licenseExpiryDate || "" },
            { label: "رقم اللوحة", value: extractedVehicleLicenseFrontData.vehiclePlateNumber || "" },
          ];
          const extractedDataString = `"Issuing Traffic Department": "${extractedVehicleLicenseFrontData.issuingTrafficDepartment || ""}", "Issuing Traffic Unit": "${extractedVehicleLicenseFrontData.issuingTrafficUnit || ""}", "Owner Name": "${extractedVehicleLicenseFrontData.ownerName || ""}", "Owner Address": "${extractedVehicleLicenseFrontData.ownerAddress || ""}", "Owner Nationality": "${extractedVehicleLicenseFrontData.ownerNationality || ""}", "License Type": "${extractedVehicleLicenseFrontData.licenseType || ""}", "License Issue Date": "${extractedVehicleLicenseFrontData.licenseIssueDate || ""}", "License Expiry Date": "${extractedVehicleLicenseFrontData.licenseExpiryDate || ""}", "Vehicle Plate Number": "${extractedVehicleLicenseFrontData.vehiclePlateNumber || ""}"`;
          
          if (onShowExtractedDataConfirmation) {
            onShowExtractedDataConfirmation(
              extractedDataFields,
              "تأكيد بيانات رخصة السيارة - الوجه الأمامي",
              "رخصة السيارة - الوجه الأمامي",
              (editedData) => {
                const finalData = convertEditedFieldsToData(editedData, extractedVehicleLicenseFrontData, "vehicle_license_front");
                const dataString = createExtractedDataString(finalData, "vehicle_license_front");
                proceedWithExtractedData(dataString, "user_uploaded_vehicle_license_front");
              },
              (editedData) => {
                const finalData = convertEditedFieldsToData(editedData, extractedVehicleLicenseFrontData, "vehicle_license_front");
                const dataString = createExtractedDataString(finalData, "vehicle_license_front");
                saveExtractedDataToSession(finalData, "vehicle_license_front");
                proceedWithExtractedData(dataString, "user_uploaded_vehicle_license_front");
              }
            );
          } else {
            proceedWithExtractedData(extractedDataString, "user_uploaded_vehicle_license_front");
          }
        } else if (flowStep === "vehicle_license_back" && extractedVehicleLicenseBackData) {
          const extractedDataFields: ExtractedDataField[] = [
            { label: "الماركة", value: extractedVehicleLicenseBackData.make || "" },
            { label: "الموديل", value: extractedVehicleLicenseBackData.model || "" },
            { label: "رقم الشاصي", value: extractedVehicleLicenseBackData.chassisNumber || "" },
            { label: "رقم المحرك", value: extractedVehicleLicenseBackData.motorNumber || "" },
            { label: "عدد أسطوانات المحرك", value: extractedVehicleLicenseBackData.engineCylinders || "" },
            { label: "نوع الوقود", value: extractedVehicleLicenseBackData.fuelType || "" },
            { label: "سعة المحرك", value: extractedVehicleLicenseBackData.engineCapacity || "" },
            { label: "المميزات", value: extractedVehicleLicenseBackData.features || "" },
            { label: "الكود", value: extractedVehicleLicenseBackData.code || "" },
            { label: "سنة الصنع", value: extractedVehicleLicenseBackData.modelYear || "" },
            { label: "نوع الهيكل", value: extractedVehicleLicenseBackData.bodyStyle || "" },
            { label: "سنة انتهاء الفحص", value: extractedVehicleLicenseBackData.inspectionExpiryYear || "" },
            { label: "لون السيارة", value: extractedVehicleLicenseBackData.vehicleColor || "" },
          ];
          const extractedDataString = `"Make": "${extractedVehicleLicenseBackData.make || ""}", "Model": "${extractedVehicleLicenseBackData.model || ""}", "Chassis Number": "${extractedVehicleLicenseBackData.chassisNumber || ""}", "Motor Number": "${extractedVehicleLicenseBackData.motorNumber || ""}", "Engine Cylinders": "${extractedVehicleLicenseBackData.engineCylinders || ""}", "Fuel Type": "${extractedVehicleLicenseBackData.fuelType || ""}", "Engine Capacity": "${extractedVehicleLicenseBackData.engineCapacity || ""}", "Features": "${extractedVehicleLicenseBackData.features || ""}", "Code": "${extractedVehicleLicenseBackData.code || ""}", "Model Year": "${extractedVehicleLicenseBackData.modelYear || ""}", "Body Style": "${extractedVehicleLicenseBackData.bodyStyle || ""}", "Inspection Expiry Year": "${extractedVehicleLicenseBackData.inspectionExpiryYear || ""}", "Vehicle Color": "${extractedVehicleLicenseBackData.vehicleColor || ""}"`;
          
          if (onShowExtractedDataConfirmation) {
            onShowExtractedDataConfirmation(
              extractedDataFields,
              "تأكيد بيانات رخصة السيارة - الوجه الخلفي",
              "رخصة السيارة - الوجه الخلفي",
              (editedData) => {
                const finalData = convertEditedFieldsToData(editedData, extractedVehicleLicenseBackData, "vehicle_license_back");
                const dataString = createExtractedDataString(finalData, "vehicle_license_back");
                proceedWithExtractedData(dataString, "user_uploaded_vehicle_license_back");
              },
              (editedData) => {
                const finalData = convertEditedFieldsToData(editedData, extractedVehicleLicenseBackData, "vehicle_license_back");
                const dataString = createExtractedDataString(finalData, "vehicle_license_back");
                saveExtractedDataToSession(finalData, "vehicle_license_back");
                proceedWithExtractedData(dataString, "user_uploaded_vehicle_license_back");
              }
            );
          } else {
            proceedWithExtractedData(extractedDataString, "user_uploaded_vehicle_license_back");
          }
        } else {
          console.warn(
            `⚠️ [Ameen] Unknown flowStep when uploading image: ${flowStep}`
          );
        }
      } catch (error) {
        console.error(
          `❌ [Ameen] Error extracting document data from backend:`,
          error
        );
        
        // Unmute microphone even if API fails
        if (onUnmuteMic) {
          console.log("🔊 [Ameen] Unmuting microphone after API error");
          onUnmuteMic();
        }
        
        // Check if this is a photo validation error (402 status code)
        const isValidationError = (error as any)?.isPhotoValidationError === true;
        const errorMessage = error instanceof Error ? error.message : String(error);
        const documentType = (error as any)?.documentType || flowStep;
        
        if (isValidationError) {
          // Send a specific message to AI about photo validation failure
          console.log(
            `📤 [Ameen] Sending [system_ui] photo_validation_failed for ${flowStep}`
          );
          client.send(
            [
              {
                text: `[system_ui] photo_validation_failed_${flowStep}: "${errorMessage}". The uploaded photo is not clear or correct. Please ask the user to upload another clear photo of the ${documentType}.`,
              },
            ],
            true
          );
        } else {
          // Still update state with image even if extraction fails (for non-validation errors)
          setCustomerNationalIdData((prev) => {
            return flowStep === "national_id_front"
              ? {
                  ...prev,
                  national_id_front_image_base64: base64,
                }
              : flowStep === "national_id_back"
              ? {
                  ...prev,
                  national_id_back_image_base64: base64,
                }
              : prev;
          });

          // Send fallback message without extracted data if extraction failed
          if (flowStep === "national_id_front" || flowStep === "national_id_back" || flowStep === "driving_license" || flowStep === "vehicle_license_front" || flowStep === "vehicle_license_back") {
            console.log(
              `📤 [Ameen] Sending [system_ui] user_uploaded_${flowStep} (extraction failed)`
            );
            client.send(
              [
                {
                  text: `[system_ui] user_uploaded_${flowStep}`,
                },
              ],
              true
            );
          }
        }
        
        if (setFlowStep) {
          setFlowStep("idle");
        }
      }

      console.log(
        `📸 [Ameen] Document image (${flowStep}) processing complete:`,
        file.name
      );
    };

    reader.readAsDataURL(file);
  };


